export const RESERVED_SUBDOMAINS = new Set([
  "account",
  "accounts",
  "admin",
  "api",
  "app",
  "assets",
  "auth",
  "billing",
  "dashboard",
  "docs",
  "help",
  "login",
  "mail",
  "market",
  "pos",
  "register",
  "settings",
  "static",
  "status",
  "store",
  "storefront",
  "stores",
  "support",
  "www"
]);

export interface TenantResolutionOptions {
  hostname: string;
  search: string;
  rootDomain?: string;
  fallbackSlug?: string;
  sessionSlug?: string | null;
  localSlug?: string | null;
  customDomainMap?: Record<string, string>;
}

export type TenantResolution =
  | { mode: "directory"; slug: null; source: "directory-host" | "root-domain" | "preview-host" | "local-host" }
  | { mode: "tenant"; slug: string; source: "custom-domain" | "subdomain" | "query" | "session" | "local" | "fallback" }
  | { mode: "invalid"; slug: null; source: "reserved-subdomain" | "invalid-tenant" | "unsupported-host" };

const normalizeHostname = (value: string) => {
  const hostname = value.trim().toLowerCase().replace(/\.$/, "");
  if (hostname.startsWith("[")) return hostname.slice(0, hostname.indexOf("]") + 1);
  return hostname.split(":")[0];
};

export const normalizeTenantSlug = (value?: string | null) => {
  const slug = value?.trim().toLowerCase();
  return slug
    && slug.length >= 3
    && slug.length <= 50
    && /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug)
    ? slug
    : null;
};

export const isValidTenantSlug = (value?: string | null): value is string => {
  const slug = normalizeTenantSlug(value);
  return Boolean(slug && !RESERVED_SUBDOMAINS.has(slug));
};

export const isLocalHostname = (hostname: string) => {
  const normalized = normalizeHostname(hostname);
  return normalized === "localhost" || normalized === "127.0.0.1" || normalized === "[::1]";
};

export const isNetlifyHostname = (hostname: string) => normalizeHostname(hostname).endsWith(".netlify.app");

const resolvePreviewTenant = (options: TenantResolutionOptions, hostSource: "preview-host" | "local-host"): TenantResolution => {
  const queryValue = new URLSearchParams(options.search).get("store");
  const querySlug = normalizeTenantSlug(queryValue);
  if (queryValue && !isValidTenantSlug(querySlug)) {
    return { mode: "invalid", slug: null, source: "invalid-tenant" };
  }
  if (querySlug) return { mode: "tenant", slug: querySlug, source: "query" };

  const candidates = [
    [options.sessionSlug, "session"],
    [options.localSlug, "local"],
    [options.fallbackSlug, "fallback"]
  ] as const;
  for (const [value, source] of candidates) {
    const slug = normalizeTenantSlug(value);
    if (slug && isValidTenantSlug(slug)) return { mode: "tenant", slug, source };
  }
  return { mode: "directory", slug: null, source: hostSource };
};

export function resolveTenant(options: TenantResolutionOptions): TenantResolution {
  const hostname = normalizeHostname(options.hostname);
  const custom = normalizeTenantSlug(options.customDomainMap?.[hostname]);
  if (custom && isValidTenantSlug(custom)) return { mode: "tenant", slug: custom, source: "custom-domain" };

  if (isLocalHostname(hostname)) return resolvePreviewTenant(options, "local-host");
  if (isNetlifyHostname(hostname)) return resolvePreviewTenant(options, "preview-host");

  const rootDomain = normalizeHostname(options.rootDomain || "pos.elmortechnologies.com");
  if (hostname === rootDomain) return { mode: "directory", slug: null, source: "root-domain" };
  if (hostname === `store.${rootDomain}`) return { mode: "directory", slug: null, source: "directory-host" };

  if (hostname.endsWith(`.${rootDomain}`)) {
    const prefix = hostname.slice(0, -(rootDomain.length + 1));
    if (!prefix || prefix.includes(".")) return { mode: "invalid", slug: null, source: "invalid-tenant" };
    const candidate = normalizeTenantSlug(prefix);
    if (!candidate) return { mode: "invalid", slug: null, source: "invalid-tenant" };
    if (RESERVED_SUBDOMAINS.has(candidate)) return { mode: "invalid", slug: null, source: "reserved-subdomain" };
    return { mode: "tenant", slug: candidate, source: "subdomain" };
  }

  return { mode: "invalid", slug: null, source: "unsupported-host" };
}
